import { useState } from 'react';
import { useSelectedContract } from 'hooks';
import { Pages } from 'constants/menu';
import { SubPagePanel } from 'components/panel/SubPagePanel';
import {
  FormCondition,
  Condition,
  ConditionType,
  TimeUnit,
  SignboardRemoval,
  SignboardRemovalRemovalType,
  StaffUpdateContractSettlementRequest,
  useStaffGetContractDates,
  useInvalidateUseStaffGetContractSettlement,
  useStaffGetContractSettlement,
  useStaffUpdateContractSettlement,
} from '@urbanx/agx.campaigns.client';
import {
  AgxBadge,
  AgxButton,
  AgxCollapsible,
  AgxDatePicker,
  AgxRadioGroup,
  AgxDivider,
  AgxSpacer,
  AgxTextInput,
  AgxTypography,
} from '@urbanx/agx.ui-components2';
import dayjs from 'dayjs';
import ConditionDate from './components/ConditionDate';
import { useFormConditionDueDate } from './components/useFormConditionDueDate';
import { RexDisconnectedModalFields } from 'pages/campaigns/components/manageCampaign/RexDataUpdateHelper';
import { useGetListingByCampaignId } from '@urbanx/agx.rex.client';
import { isEqual } from 'lodash-es';
import { useGetSpecialConditionsInfo } from 'hooks/useGetSpecialConditionsInfo';
import useRexDisconnectedModal from 'pages/campaigns/components/manageCampaign/useRexDisconnectedModal';
import slugify from 'slugify';
import { dateOnlyToDate, toDateOnlyString } from '@urbanx/agx.ui-components2';
import { ContractInformationPage } from './ContractInformationPage';
import { useContractInformationEditor } from './useContractInformationEditor';

const Settlement = () => {
  const { contract, campaignId } = useSelectedContract();
  const [isEditingSettlementDate, setIsEditingSettlementDate] = useState(false);
  const invalidateUseStaffGetContractSettlement = useInvalidateUseStaffGetContractSettlement();

  const { specialConditionIdToClauseNumberMap } = useGetSpecialConditionsInfo(
    campaignId,
    contract?.contractId
  );

  const {
    data: contractSettlement,
    isPending,
    isError,
  } = useStaffGetContractSettlement(
    campaignId || '',
    contract?.contractId || '',
    !!campaignId && !!contract?.contractId
  );

  const { data: contractDates } = useStaffGetContractDates(
    campaignId || '',
    contract?.contractId || '',
    !!campaignId && !!contract?.contractId
  );

  const { modal: rexDisconnectedModal, openWithChanges } = useRexDisconnectedModal();

  const { staffUpdateContractSettlement } = useStaffUpdateContractSettlement({
    onSuccess: () => {},
    onError: () => {},
  });

  const { data: rexListing, isPending: isPendingRexListing } = useGetListingByCampaignId(
    campaignId || '',
    campaignId !== undefined
  );

  type SettlementDraft = {
    settlementInfo: FormCondition | undefined;
    signboardRemoval: SignboardRemoval | undefined;
  };

  const source: SettlementDraft | undefined = contractSettlement
    ? {
        settlementInfo: contractSettlement.settlementInfo,
        signboardRemoval: contractSettlement.signboardRemoval,
      }
    : undefined;

  const editor = useContractInformationEditor({
    resourceKey: `${campaignId ?? ''}:${contract?.contractId ?? ''}:settlement`,
    source,
    isPending,
    isError,
    persist: async ({ draft }) => {
      const request: StaffUpdateContractSettlementRequest = {
        settlementInfo: draft.settlementInfo,
        signboardRemoval: draft.signboardRemoval,
      };
      const response = await staffUpdateContractSettlement({
        campaignId: campaignId!,
        contractId: contract!.contractId,
        body: request,
      });

      if (!response?.success) return { accepted: false, errors: response?.errors ?? [] };

      if (!rexListing && isPendingRexListing === false) {
        openWithChanges(
          !isEqual(prevSettlementDueDate.dueDateString, currSettlementDueDate.dueDateString)
            ? [
                {
                  field: RexDisconnectedModalFields.SettlementDate,
                  value: currSettlementDueDate.dueDateString,
                },
              ]
            : []
        );
      }

      return { accepted: true };
    },
    refresh: {
      authority: 'source',
      refreshSource: () =>
        invalidateUseStaffGetContractSettlement(campaignId!, contract!.contractId),
    },
  });

  const prevSettlementDueDate = useFormConditionDueDate(
    source?.settlementInfo,
    contractDates?.contractDate
  );
  const currSettlementDueDate = useFormConditionDueDate(
    editor.draft?.settlementInfo,
    contractDates?.contractDate
  );
  const [editedSettlementDate, setEditedSettlementDate] = useState<Date>();

  if (!editor.draft) {
    return (
      <ContractInformationPage currentPage={Pages.SETTLEMENT} editor={editor}>
        {null}
      </ContractInformationPage>
    );
  }

  const { settlementInfo, signboardRemoval } = editor.draft;

  const settlementBadgeText = (() => {
    if (!settlementInfo) return '';

    if (
      settlementInfo.condition === Condition.NdaysFromDate &&
      settlementInfo.conditionType === ConditionType.Contract &&
      settlementInfo.daysFromDate &&
      settlementInfo.daysFromDate > 0 &&
      (settlementInfo.fromDateTimeUnit === TimeUnit.Days ||
        settlementInfo.fromDateTimeUnit === TimeUnit.Weeks)
    ) {
      return `${settlementInfo.daysFromDate} ${settlementInfo.fromDateTimeUnit.toLowerCase()} from contract date`;
    }

    return currSettlementDueDate.dueDateString
      ? dayjs(currSettlementDueDate.dueDateString).format('Do MMM')
      : '';
  })();

  const onEditSettlementDate = () => {
    if (isEditingSettlementDate) {
      if (!editedSettlementDate) return;

      editor.update((current) => ({
        ...current,
        settlementInfo: {
          ...current.settlementInfo,
          condition: Condition.OnSpecificDate,
          referenceDate: toDateOnlyString(editedSettlementDate),
          daysFromDate: undefined,
          conditionType: undefined,
          specialConditionId: undefined,
        },
      }));
      setIsEditingSettlementDate(false);
      return;
    }

    setEditedSettlementDate(dateOnlyToDate(currSettlementDueDate.dueDateString) ?? undefined);
    setIsEditingSettlementDate(true);
  };

  const settlementDate = isEditingSettlementDate
    ? editedSettlementDate
    : dateOnlyToDate(currSettlementDueDate.dueDateString);

  return (
    <ContractInformationPage currentPage={Pages.SETTLEMENT} editor={editor}>
      <AgxCollapsible
        key={`SettlementPanel${isPending ? 'Loaded' : ''}`}
        variant="withDivider"
        enableToggle
        open
        customTitle={
          <AgxSpacer fill direction="horizontal" gap="xs" contentAlignment="center">
            <AgxTypography
              id={`condition-${slugify('Settlement')}`}
              type="title"
              size="large"
              color="var(--text-primary)"
            >
              Settlement
            </AgxTypography>
            {settlementBadgeText && (
              <AgxBadge type="colourfill" colour="yellow" size="md" text={settlementBadgeText} />
            )}
          </AgxSpacer>
        }
        content={
          <>
            {settlementInfo && (
              <>
                <AgxSpacer
                  direction="horizontal"
                  gap="spacing-md"
                  fill
                  padding={{ top: 'spacing-md', bottom: 'spacing-3xl' }}
                >
                  <ConditionDate
                    id="settlementInfo"
                    condition={settlementInfo}
                    specialConditionIdToClauseNumberMap={specialConditionIdToClauseNumberMap ?? {}}
                  />
                </AgxSpacer>
                <AgxDivider variant="horizontal" />
              </>
            )}
            <AgxSpacer
              direction="horizontal"
              gap="spacing-md"
              fill
              padding={{ top: 'spacing-md', bottom: 'spacing-3xl' }}
              contentAlignment="end"
            >
              <AgxDatePicker
                id="settlementInfo-due-date"
                stretch
                disabled={!isEditingSettlementDate}
                optionalText=""
                label="Due Date"
                value={settlementDate ? dayjs(settlementDate).format('YYYY-MM-DD') : ''}
                onChange={(value) => {
                  setEditedSettlementDate(value ?? undefined);
                }}
              />
              <AgxButton
                id="settlementInfo-edit-due-date"
                variant="hollow"
                text={isEditingSettlementDate ? 'Save' : 'Edit'}
                onClick={onEditSettlementDate}
              />
            </AgxSpacer>
          </>
        }
      />
      <SubPagePanel
        key={`SignboardRemovalPanel${isPending ? 'Loaded' : ''}`}
        panelTitle="Signboard Removal"
        expandable
        defaultExpanded
      >
        <AgxRadioGroup
          id="signboardRemoval_RemovalType"
          key={`signboardRemoval_RemovalType${isPending ? 'Loaded' : ''}`}
          layout="stacked"
          value={signboardRemoval?.removalType || ''}
          onChange={(val) => {
            if (val === signboardRemoval?.removalType) return;
            editor.update((current) => ({
              ...current,
              signboardRemoval: {
                ...current.signboardRemoval,
                removalType: val as SignboardRemovalRemovalType,
              },
            }));
          }}
          options={[
            {
              label: 'Upon settlement',
              value: SignboardRemovalRemovalType.UponSettlement,
            },
            {
              label: 'On Specific Date',
              value: SignboardRemovalRemovalType.OnSpecificDate,
            },
            {
              label: 'No signboard installed',
              value: SignboardRemovalRemovalType.NoSignboardInstalled,
            },
          ]}
          subContent={
            signboardRemoval?.removalType === SignboardRemovalRemovalType.OnSpecificDate
              ? [
                  <AgxSpacer
                    direction="vertical"
                    key={`signboardRemoval_specificDate_Column${isPending ? 'Loaded' : ''}`}
                    gap="spacing-md"
                    padding={{
                      top: 'spacing-xl',
                      bottom: 'spacing-xl',
                      left: 'spacing-xl',
                      right: 'spacing-xl',
                    }}
                  >
                    <AgxTextInput
                      id="signboardRemoval_specificDate"
                      key={`signboardRemoval_specificDate${isPending ? 'Loaded' : ''}`}
                      onInputValueChange={({ value }) => {
                        editor.update((current) => ({
                          ...current,
                          signboardRemoval: {
                            ...current.signboardRemoval,
                            specificDate: value || undefined,
                          },
                        }));
                      }}
                      required
                      hideOptionalText
                      type="date"
                      defaultValue={
                        signboardRemoval?.specificDate
                          ? dayjs(signboardRemoval?.specificDate).format('YYYY-MM-DD')
                          : undefined
                      }
                      errorText={
                        editor.dirty && !signboardRemoval?.specificDate ? 'Enter a date' : undefined
                      }
                    />
                  </AgxSpacer>,
                ]
              : undefined
          }
        />
      </SubPagePanel>
      {rexDisconnectedModal}
    </ContractInformationPage>
  );
};

export default Settlement;
